import type {
    CampaignCreateBody,
    CampaignDto,
    CampaignsDto,
    CampaignUpdateBody,
} from "@campaign/data/dto/campaign.dto";
import type { CropTypesDto } from "@campaign/data/dto/crop-type.dto";
import { CampaignMapper } from "@campaign/data/mappers/campaign.mapper";
import { CropTypeMapper } from "@campaign/data/mappers/crop-type.mapper";
import type { CampaignCreateError } from "@campaign/domain/errors/campaign-create.error";
import type { CampaignUpdateError } from "@campaign/domain/errors/campaign-update.error";
import type { CampaignFiltersQuery } from "@campaign/domain/models/campaign-filters-query.model";
import type {
    Campaign,
    CampaignCreate,
    CampaignFilter,
    Campaigns,
    CampaignUpdate,
} from "@campaign/domain/models/campaign.model";
import type { CropTypes } from "@campaign/domain/models/crop-type.model";
import { webCoreTypes } from "@core/core-types.di";
import { FallbackError } from "@core/domain/errors/fallback.error";
import type { Pagination } from "@core/domain/models/pagination.model";
import type { Nullable } from "@core/domain/types/nullable.type";
import type { IotApiClient } from "@iot-platform/core";
import { inject, injectable } from "inversify";
import type { ResultAsync } from "neverthrow";

@injectable()
export class CampaignDatasource {
    static readonly CAMPAIGN_PATH = "/campaigns";
    static readonly CROP_TYPE_PATH = "/crop_types";

    constructor(
        @inject(webCoreTypes.infrastructure.IotApiClient)
        private readonly iotApiClient: IotApiClient,
        @inject(CampaignMapper)
        private readonly campaignMapper: CampaignMapper,
        @inject(CropTypeMapper)
        private readonly cropTypeMapper: CropTypeMapper,
    ) {}

    fetchAll(
        pagination: Pagination,
        campaignFilter?: CampaignFilter,
    ): ResultAsync<Campaigns, FallbackError> {
        const query: CampaignFiltersQuery = {
            page: pagination.current,
            per_page: pagination.perPage,
            date: campaignFilter?.date,
            crop_type_id: campaignFilter?.cropTypeId,
            name: campaignFilter?.name,
            station_id: campaignFilter?.stationId?.length
                ? campaignFilter.stationId
                : undefined,
        };

        return this.iotApiClient.http
            .get<CampaignsDto>(CampaignDatasource.CAMPAIGN_PATH, {
                query,
            })

            .mapErr(() => new FallbackError())
            .map((response) => this.campaignMapper.mapList(response.data));
    }

    fetchById(
        campaignId: string,
    ): ResultAsync<Nullable<Campaign>, FallbackError> {
        return this.iotApiClient.http
            .get<CampaignDto>(
                `${CampaignDatasource.CAMPAIGN_PATH}/${campaignId}/`,
            )
            .mapErr(() => new FallbackError())
            .andThen((response) =>
                this.campaignMapper
                    .map(response.data)
                    .mapErr(() => new FallbackError()),
            );
    }

    create(
        createCampaign: CampaignCreate,
    ): ResultAsync<Campaign, CampaignCreateError> {
        const campaignCreateBody: CampaignCreateBody = {
            campaign: {
                name: createCampaign.name,
                start_date: createCampaign.startDate,
                end_date: createCampaign.endDate,
                crop_type_id: createCampaign.cropTypeId,
                station_ids: createCampaign.stationIds,
            },
        };

        return this.iotApiClient.http
            .post<CampaignDto>(
                CampaignDatasource.CAMPAIGN_PATH,
                campaignCreateBody,
            )
            .mapErr(() => new FallbackError())
            .andThen((response) => this.campaignMapper.map(response.data));
    }

    update(
        campaignId: string,
        updateCampaign: CampaignUpdate,
    ): ResultAsync<Campaign, CampaignUpdateError> {
        const campaignUpdateBody: CampaignUpdateBody = {
            campaign: {
                name: updateCampaign.name,
                start_date: updateCampaign.startDate,
                end_date: updateCampaign.endDate,
                crop_type_id: updateCampaign.cropTypeId,
                station_ids: updateCampaign.stationIds,
            },
        };

        return this.iotApiClient.http
            .put<CampaignDto>(
                `${CampaignDatasource.CAMPAIGN_PATH}/${campaignId}`,
                campaignUpdateBody,
            )
            .mapErr(() => new FallbackError())
            .andThen((response) => this.campaignMapper.map(response.data));
    }

    delete(campaignId: string): ResultAsync<true, FallbackError> {
        return this.iotApiClient.http
            .delete(`${CampaignDatasource.CAMPAIGN_PATH}/${campaignId}/`)
            .mapErr(() => new FallbackError())
            .map(() => true);
    }

    fetchAllCropTypes(): ResultAsync<CropTypes, FallbackError> {
        return this.iotApiClient.http
            .get<CropTypesDto>(CampaignDatasource.CROP_TYPE_PATH)
            .mapErr(() => new FallbackError())
            .map((response) => this.cropTypeMapper.mapList(response.data));
    }
}
